import { useMemo } from 'react';
import { Award, ChevronRight, Headset } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { Button } from '@sandtech-hub-monorepo/ui-kit';
import { GetSavannaCoursesForProgramDocument } from '@sandtech-hub-monorepo/ehub-client';

import { EnrolledProgramSummary } from '../../data/programs';
import { supportUrlFor } from '../../data/useLearnerPath';
import { InteractiveStructure } from './InteractiveStructure';
import { ReactComponent as ProgramIcon } from '../../assets/Icons/program-icon.svg';

interface Props {
  /** Narrow on purpose: a real enrollment has a name, a description and
   * whatever courses have been joined to it, and none of the rest. */
  program: EnrolledProgramSummary;
  /** The eHub program id, for reading its Savanna curricula. */
  programId: string;
  /** The learner's sandId, so the structure carries their progress. */
  sandId: string;
  /**
   * `CourseDefinition` ids the learner has graduated in this program. The
   * total course count is read here from Savanna rather than passed in,
   * since that is the only place a course LIST exists for a GSA program —
   * `InteractiveStructure` below reads the same document, so this costs no
   * extra request once either has resolved it.
   */
  completedCourseIds: string[];
  /** Subscription active/expiring — paid modules are openable. */
  paidUnlocked: boolean;
  /** Subscription expired — progress is preserved but content re-locks. */
  expired: boolean;
  onSubscribe: () => void;
  onGoToCommunity: () => void;
  /** Opens the program's detail page. The title is the only thing that does. */
  onOpenProgram: () => void;
}

/**
 * An enrolled program with its progress, community link and curriculum.
 * Shared by the Home dashboard ("My programs") and the Learning "Current
 * Programs" tab.
 *
 * The curriculum is the same interactive tree the program detail page shows
 * an enrolled learner - courses fetched on expand, activities linking into
 * Savanna - rather than a second rendering of the same data that could drift
 * from it.
 *
 * The headline row counts COURSES, not modules — `CourseEnrollment.status`
 * is the only per-learner completion signal a GSA program has, and it is
 * course-grained. The total comes from the same Savanna course list
 * `InteractiveStructure` renders below, so this costs no extra request once
 * either has resolved it.
 */
export const EnrolledProgramCard = ({
  program,
  programId,
  sandId,
  completedCourseIds,
  paidUnlocked,
  expired,
  onSubscribe,
  onGoToCommunity,
  onOpenProgram,
}: Props) => {
  const { data: coursesData } = useQuery(GetSavannaCoursesForProgramDocument, {
    variables: { programId },
    fetchPolicy: 'cache-first',
  });

  const totalCourses = coursesData?.savannaCoursesForProgram.length ?? 0;
  const completedCourses = useMemo(() => {
    const courseDefinitionIds = new Set(
      coursesData?.savannaCoursesForProgram.map((c) => c.courseDefinitionId)
    );
    return completedCourseIds.filter((id) => courseDefinitionIds.has(id))
      .length;
  }, [coursesData, completedCourseIds]);

  // Guard the divide as well as the display: 0/0 is NaN, which renders as a
  // literal "NaN%" and a broken bar width.
  const pct =
    totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

  return (
    <div className="rounded-2xl border border-border-secondary bg-card p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex h-14 w-16 shrink-0 items-center justify-center">
          <ProgramIcon className="h-full w-full" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onOpenProgram}
              className="text-left text-lg font-semibold text-heading hover:text-primary hover:underline"
            >
              {program.title}
            </button>
            {expired && (
              <span className="text-sm font-medium text-destructive">
                Access Expired
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground sm:mr-8">
            {program.description}
          </p>
          {totalCourses > 0 && (
            // The bar alone wraps onto its own row on mobile: it is the only
            // one of the four given `w-full`, so `flex-wrap` breaks the line
            // there regardless of the others' widths. `order` reshuffles it
            // between "after the label" (mobile) and "back in its original
            // position" (sm+) without touching DOM/reading order.
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="order-1 shrink-0 text-sm font-medium text-foreground">
                {completedCourses} of {totalCourses} completed courses
              </span>
              <div className="order-4 h-2 w-full overflow-hidden rounded-full bg-muted sm:order-2 sm:w-auto sm:max-w-xs sm:flex-1">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="order-2 shrink-0 text-sm text-muted-foreground sm:order-3">
                {pct}%
              </span>
              <Award className="order-3 h-4 w-4 shrink-0 text-muted-foreground sm:order-4" />
            </div>
          )}
        </div>

        <div className="flex w-full shrink-0 flex-col gap-5 sm:w-auto sm:items-center">
          <Button
            onClick={onGoToCommunity}
            className="w-full rounded-full px-6 sm:w-auto"
          >
            Go to Community
          </Button>
          <Link
            // Always GSA's own URL: this card only ever renders for GSA
            // enrollments (`useGsaEnrollments` filters to non-university
            // programs).
            to={supportUrlFor(false)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-1.5 text-sm font-medium text-primary sm:w-auto"
          >
            <Headset className="h-4 w-4" />
            Get Support
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/*
        No "show all" switch any more. It capped the list at one course to
        avoid a wall of them, and the tree below is lazy - every course past
        the first is collapsed and unfetched - so listing them all costs
        nothing and hiding them only buries what the learner came for.
      */}
      <div className="mt-5 flex flex-col gap-4 border-t border-border-secondary pt-4">
        <InteractiveStructure
          programId={programId}
          sandId={sandId}
          covered={paidUnlocked}
          onSubscribe={onSubscribe}
        />
      </div>
    </div>
  );
};
